import React, { Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchScansDaily, type ScansDailyPoint } from '../../diagnostics/scans.api.js';

const ScansDailyChart = lazy(() => import('./ScansDailyChart.js'));

export function ScansCard({
  baseUrl,
  siteKey,
  from,
  to,
  comparePrev,
}: {
  baseUrl: string;
  siteKey: string;
  from: string;
  to: string;
  comparePrev: boolean;
}) {
  const curQ = useQuery({
    queryKey: ['cmp', 'scans', 'daily', baseUrl, siteKey, from, to],
    queryFn: () => fetchScansDaily(baseUrl, siteKey, `${from}T00:00:00Z`, `${to}T23:59:59Z`),
    enabled: Boolean(baseUrl),
    staleTime: 60_000,
  });

  const prevRange = React.useMemo(() => {
    try {
      const fromDate = new Date(`${from}T00:00:00Z`);
      const toDate = new Date(`${to}T23:59:59Z`);
      const spanMs = toDate.getTime() - fromDate.getTime() + 1;
      const prevTo = new Date(fromDate.getTime() - 1);
      const prevFrom = new Date(prevTo.getTime() - spanMs + 1);
      return { prevFromISO: prevFrom.toISOString(), prevToISO: prevTo.toISOString() };
    } catch {
      return { prevFromISO: undefined, prevToISO: undefined } as const;
    }
  }, [from, to]);

  const prevQ = useQuery({
    queryKey: [
      'cmp',
      'scans',
      'daily',
      'prev',
      baseUrl,
      siteKey,
      prevRange.prevFromISO ?? null,
      prevRange.prevToISO ?? null,
    ],
    queryFn: () =>
      fetchScansDaily(
        baseUrl,
        siteKey,
        prevRange.prevFromISO as string,
        prevRange.prevToISO as string,
      ),
    enabled: Boolean(baseUrl && comparePrev && prevRange.prevFromISO && prevRange.prevToISO),
    staleTime: 60_000,
  });

  if (!curQ.data || curQ.data.length === 0) return null;

  const sum = (arr: ScansDailyPoint[], k: keyof ScansDailyPoint) =>
    arr.reduce((a, p) => a + (Number(p[k]) || 0), 0);
  const runs = sum(curQ.data, 'runs');
  const ok = sum(curQ.data, 'ok_runs');
  const okRate = runs > 0 ? (ok / runs) * 100 : 0;
  const new3p = sum(curQ.data, 'new_third_parties');
  const newCookies = sum(curQ.data, 'new_cookie_names');
  let okDelta: number | null = null;
  let new3pDelta: number | null = null;
  let newCookiesDelta: number | null = null;
  if (comparePrev && prevQ.data && prevQ.data.length) {
    const pruns = sum(prevQ.data, 'runs');
    const pok = sum(prevQ.data, 'ok_runs');
    const p3p = sum(prevQ.data, 'new_third_parties');
    const pcookies = sum(prevQ.data, 'new_cookie_names');
    const pokRate = pruns > 0 ? (pok / pruns) * 100 : 0;
    okDelta = okRate - pokRate;
    new3pDelta = new3p - p3p;
    newCookiesDelta = newCookies - pcookies;
  }

  return (
    <div className="rounded-lg border p-4 text-sm">
      <h2 className="text-lg font-semibold mb-2">Scans Daily</h2>
      <div className="mb-2 text-xs text-gray-600 flex gap-4">
        <span>
          OK rate: {okRate.toFixed(1)}%
          {okDelta !== null && (
            <span className={okDelta >= 0 ? 'text-green-600' : 'text-red-600'}>
              {' '}
              ({okDelta >= 0 ? '↑' : '↓'} {Math.abs(okDelta).toFixed(1)}%)
            </span>
          )}
        </span>
        <span>
          New 3P: {new3p}
          {new3pDelta !== null && (
            <span className={new3pDelta <= 0 ? 'text-green-600' : 'text-red-600'}>
              {' '}
              ({new3pDelta >= 0 ? '+' : '−'}
              {Math.abs(new3pDelta)})
            </span>
          )}
        </span>
        <span>
          New cookies: {newCookies}
          {newCookiesDelta !== null && (
            <span className={newCookiesDelta <= 0 ? 'text-green-600' : 'text-red-600'}>
              {' '}
              ({newCookiesDelta >= 0 ? '+' : '−'}
              {Math.abs(newCookiesDelta)})
            </span>
          )}
        </span>
      </div>
      <div className="h-56">
        <Suspense fallback={<div className="text-xs text-gray-500">Loading chart…</div>}>
          <ScansDailyChart data={curQ.data} />
        </Suspense>
      </div>
      <div className="mt-2 flex gap-2 text-xs">
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            try {
              const u = new URL('/api/admin/scans/summary/daily', baseUrl);
              u.searchParams.set('siteKey', siteKey);
              u.searchParams.set('from', `${from}T00:00:00Z`);
              u.searchParams.set('to', `${to}T23:59:59Z`);
              void navigator.clipboard?.writeText(u.toString());
            } catch {
              /* noop */
            }
          }}
        >
          Copy API
        </button>
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            const rows = curQ.data as ScansDailyPoint[];
            const header = 'day,runs,ok_runs,new_third_parties,new_cookie_names\n';
            const body = rows
              .map(
                (r) =>
                  `${r.day},${r.runs},${r.ok_runs},${r.new_third_parties},${r.new_cookie_names}`,
              )
              .join('\n');
            const blob = new Blob([header + body], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `scans-${siteKey}-${from}-${to}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }}
        >
          Download CSV
        </button>
      </div>
    </div>
  );
}

export default ScansCard;
